import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, Sparkles } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

export default function TrattativaModal({ open, onOpenChange, vehicle, defaultType = "acquisto" }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState("form"); // form | success
  const [submitting, setSubmitting] = useState(false);
  const [createdPratica, setCreatedPratica] = useState(null);

  const [form, setForm] = useState({
    type: defaultType,
    quantity: 1,
    country_to: "IT",
    target_price: "",
    transport_required: true,
    delivery_city: "",
    notes: "",
    preferred_contact: "email",
    urgency: "normale",
  });

  const reset = () => {
    setStep("form");
    setCreatedPratica(null);
    setForm({
      type: defaultType,
      quantity: 1,
      country_to: "IT",
      target_price: "",
      transport_required: true,
      delivery_city: "",
      notes: "",
      preferred_contact: "email",
      urgency: "normale",
    });
  };

  const handleClose = (isOpen) => {
    if (!isOpen) reset();
    onOpenChange(isOpen);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!vehicle) return;
    setSubmitting(true);
    try {
      const body = {
        vehicle_id: vehicle.id,
        type: form.type,
        quantity: parseInt(form.quantity) || 1,
        country_to: form.country_to,
        target_price: form.target_price ? parseFloat(form.target_price) : null,
        transport_required: form.transport_required,
        delivery_city: form.delivery_city || null,
        notes: form.notes || null,
        preferred_contact: form.preferred_contact,
        urgency: form.urgency,
      };
      const r = await api.post("/pratiche", body);
      setCreatedPratica(r.data);
      setStep("success");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        data-testid="trattativa-modal"
        className="sm:max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        {step === "form" && (
          <>
            <DialogHeader>
              <div className="text-[10px] uppercase tracking-[0.2em] text-blue-600 font-semibold">
                {t("trattativa.overline")}
              </div>
              <DialogTitle className="font-heading text-2xl tracking-tight">
                {t("trattativa.title")}
              </DialogTitle>
              <DialogDescription className="text-slate-500">
                {vehicle ? `${vehicle.brand} ${vehicle.model} ${vehicle.trim || ""}` : ""}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={submit} className="space-y-5 mt-2" data-testid="trattativa-form">
              {/* Type */}
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  {t("trattativa.type_label")}
                </Label>
                <RadioGroup
                  value={form.type}
                  onValueChange={(v) => setForm({ ...form, type: v })}
                  className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2"
                  data-testid="trattativa-type-group"
                >
                  {[
                    { v: "acquisto", key: "type_acquisto" },
                    { v: "offerta_completa", key: "type_offerta_completa" },
                    { v: "solo_verifica", key: "type_solo_verifica" },
                    { v: "domanda", key: "type_domanda" },
                  ].map((opt) => (
                    <label
                      key={opt.v}
                      htmlFor={`trattativa-type-${opt.v}`}
                      className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition-colors ${
                        form.type === opt.v
                          ? "border-blue-500 bg-blue-50/50"
                          : "border-slate-200 hover:bg-slate-50"
                      }`}
                    >
                      <RadioGroupItem
                        value={opt.v}
                        id={`trattativa-type-${opt.v}`}
                        data-testid={`trattativa-type-${opt.v}`}
                      />
                      <div className="text-sm font-medium text-slate-900 leading-snug">
                        {t(`trattativa.${opt.key}`)}
                      </div>
                    </label>
                  ))}
                </RadioGroup>
              </div>

              {/* Row 1 — Quantity is HIDDEN for single-vehicle listings (all current listings).
                    The default `1` is sent silently. Kept as JSX with `vehicle?.listing_type === "stock"`
                    guard so future stock listings can bring the field back. */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {vehicle?.listing_type === "stock" ? (
                  <div>
                    <Label htmlFor="quantity" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                      {t("trattativa.quantity")}
                    </Label>
                    <Input
                      id="quantity"
                      type="number"
                      min={1}
                      value={form.quantity}
                      onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                      data-testid="trattativa-quantity"
                      className="mt-1 h-10"
                    />
                  </div>
                ) : null}
                <div className={vehicle?.listing_type === "stock" ? "" : "sm:col-span-2"}>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    {t("trattativa.country_to")}
                  </Label>
                  <Select
                    value={form.country_to}
                    onValueChange={(v) => setForm({ ...form, country_to: v })}
                  >
                    <SelectTrigger data-testid="trattativa-country-to" className="mt-1 h-10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="IT">🇮🇹 Italia</SelectItem>
                      <SelectItem value="DE">🇩🇪 Germania</SelectItem>
                      <SelectItem value="OTHER">{t("trattativa.country_other")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Row 2 */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="target_price" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    {t("trattativa.target_price")}
                  </Label>
                  <Input
                    id="target_price"
                    type="number"
                    placeholder={vehicle ? String(vehicle.price_eur) : ""}
                    value={form.target_price}
                    onChange={(e) => setForm({ ...form, target_price: e.target.value })}
                    data-testid="trattativa-target-price"
                    className="mt-1 h-10"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    {t("trattativa.urgency")}
                  </Label>
                  <Select
                    value={form.urgency}
                    onValueChange={(v) => setForm({ ...form, urgency: v })}
                  >
                    <SelectTrigger data-testid="trattativa-urgency" className="mt-1 h-10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bassa">{t("trattativa.urgency_low")}</SelectItem>
                      <SelectItem value="normale">{t("trattativa.urgency_normal")}</SelectItem>
                      <SelectItem value="alta">{t("trattativa.urgency_high")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Transport */}
              <div className="rounded-xl border border-slate-200 p-4">
                <div className="flex items-center justify-between">
                  <Label htmlFor="transport" className="text-sm font-semibold text-slate-900">
                    {t("trattativa.transport_required")}
                  </Label>
                  <Switch
                    id="transport"
                    checked={form.transport_required}
                    onCheckedChange={(v) => setForm({ ...form, transport_required: v })}
                    data-testid="trattativa-transport"
                  />
                </div>
                {form.transport_required && (
                  <div className="mt-3">
                    <Label htmlFor="delivery_city" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                      {t("trattativa.delivery_city")}
                    </Label>
                    <Input
                      id="delivery_city"
                      placeholder={t("trattativa.delivery_city_placeholder")}
                      value={form.delivery_city}
                      onChange={(e) => setForm({ ...form, delivery_city: e.target.value })}
                      data-testid="trattativa-delivery-city"
                      className="mt-1 h-10"
                    />
                  </div>
                )}
              </div>

              {/* Preferred contact */}
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  {t("trattativa.preferred_contact")}
                </Label>
                <RadioGroup
                  value={form.preferred_contact}
                  onValueChange={(v) => setForm({ ...form, preferred_contact: v })}
                  className="mt-2 flex flex-wrap gap-2"
                  data-testid="trattativa-preferred-contact"
                >
                  {["email", "telefono", "whatsapp"].map((c) => (
                    <label
                      key={c}
                      htmlFor={`contact-${c}`}
                      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium cursor-pointer transition-colors ${
                        form.preferred_contact === c
                          ? "border-blue-500 bg-blue-50 text-blue-700"
                          : "border-slate-200 text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      <RadioGroupItem
                        value={c}
                        id={`contact-${c}`}
                        className="w-3 h-3"
                        data-testid={`trattativa-contact-${c}`}
                      />
                      {t(`trattativa.contact_${c}`)}
                    </label>
                  ))}
                </RadioGroup>
              </div>

              {/* Notes */}
              <div>
                <Label htmlFor="notes" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  {t("trattativa.notes")}
                </Label>
                <Textarea
                  id="notes"
                  rows={3}
                  placeholder={t("trattativa.notes_placeholder")}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  data-testid="trattativa-notes"
                  className="mt-1"
                />
              </div>

              {/* Disclaimer */}
              <div
                data-testid="trattativa-disclaimer"
                className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900 leading-relaxed"
              >
                <strong className="font-semibold">
                  {t("trattativa.disclaimer_strong")}
                </strong>{" "}
                {t("trattativa.disclaimer_rest")}
              </div>

              {/* B4 — external link to Tariffario (opens in a new tab so the modal state is preserved). */}
              <div className="text-xs">
                <a
                  href="/tariffario"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="trattativa-view-tariff"
                  className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 font-semibold"
                >
                  {t("trattativa.view_tariff")} <span aria-hidden>↗</span>
                </a>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => handleClose(false)}
                  data-testid="trattativa-cancel"
                >
                  {t("common.cancel")}
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="trattativa-submit"
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-2" />
                  )}
                  {t("trattativa.submit")}
                </Button>
              </div>
            </form>
          </>
        )}

        {step === "success" && (
          <div className="py-6 text-center" data-testid="trattativa-success">
            <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="w-7 h-7 text-emerald-600" />
            </div>
            <h3 className="mt-4 font-heading text-xl tracking-tight text-slate-950">
              {t("trattativa.success_title")}
            </h3>
            <p className="mt-2 text-sm text-slate-600 max-w-md mx-auto leading-relaxed">
              {t("trattativa.success_desc")}
            </p>
            {createdPratica?.code && (
              <div
                data-testid="trattativa-code"
                className="mt-4 inline-flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-full px-4 py-1.5 text-xs font-mono font-semibold text-slate-700"
              >
                {createdPratica.code}
              </div>
            )}
            <p className="mt-6 text-xs text-slate-500 italic max-w-md mx-auto">
              {t("trattativa.tagline")}
            </p>
            <div className="mt-6 flex items-center justify-center gap-2">
              <Button
                variant="outline"
                onClick={() => handleClose(false)}
                data-testid="trattativa-close-success"
              >
                {t("common.close")}
              </Button>
              <Button
                onClick={() => {
                  handleClose(false);
                  if (createdPratica?.id) navigate(`/pratiche/${createdPratica.id}`);
                }}
                data-testid="trattativa-open-pratica"
                className="bg-blue-600 hover:bg-blue-700"
              >
                {t("trattativa.view_pratica")}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
